import React, { Fragment, useEffect } from 'react';
import Head from 'next/head';
import DEFAULT_IMAGE from 'public/static/images/fb_share.jpg';
import { useRouter } from 'next/router';
import { logPageview, setUrl } from 'app/utils/linkpulse';
import { NextRouter } from 'next/dist/shared/lib/router/router';
import { join, pathNoQueryHash } from '../../utils/urlUtil';
import { getFeedUrlForPath, FeedUrl } from '../../utils/feedUtils';
import env from '../../env';

export type MetaValues = {
    title: string;
    description: string;
    keywords: string;
    image: any;
};

export const META_DEFAULT_VALUES: MetaValues = {
    title: 'SVT Play',
    description: 'Se dina favoritprogram när du vill i SVT Play - fri television på nätet.',
    keywords: 'svt, play, svtplay, program, tv, television',
    image: DEFAULT_IMAGE,
};

type ReactHelmetFeedsProps = {
    title: string;
    path?: string;
};

export const ReactHelmetFeeds = ({ title, path }: ReactHelmetFeedsProps): JSX.Element => {
    if (!path) {
        return null;
    }

    const urlFeed: Array<FeedUrl> = getFeedUrlForPath(path, title);

    return (
        <>
            {urlFeed.map((item: FeedUrl, i: number) => (
                <Head key={`feed-${i}`}>
                    <link
                        rel="alternate"
                        type="application/rss+xml"
                        title={item.title || title}
                        href={join(env.BASE_URL, item.path, '/rss.xml')}
                    />
                    <link
                        rel="alternate"
                        type="application/atom+xml"
                        title={item.title || title}
                        href={join(env.BASE_URL, item.path, '/atom.xml')}
                    />
                </Head>
            ))}
        </>
    );
};

type ReactHelmetPlayProps = {
    title?: string;
    description?: string;
    keywords?: string;
    pathForFeeds?: string;
    image?: string;
    children?: any;
    noIndex?: boolean;
};

export const ReactHelmetPlay = ({
    title,
    description,
    keywords,
    pathForFeeds,
    image,
    children,
    noIndex,
}: ReactHelmetPlayProps): JSX.Element => {
    const router = useRouter();

    const combinedTitle: string = title !== 'SVT Play' ? `${title} | SVT Play` : title;

    useEffect(() => {
        if (title && typeof window !== 'undefined' && env.FLAG_ENABLE_LINKPULSE) {
            const isIndex = title === 'SVT Play' && router.pathname === '/' && !image;

            if (isIndex || title !== 'SVT Play') {
                logPageview({
                    title: combinedTitle,
                    image: image || DEFAULT_IMAGE,
                    url: encodeURI(window.location.href),
                });
                const pathname = pathNoQueryHash(router.asPath);

                setUrl(join(env.BASE_URL, pathname));
            }
        }
    }, [title, combinedTitle, image, router.pathname, router.asPath]);

    return (
        <>
            {noIndex && (
                <Head>
                    <meta key="robots" name="robots" content="noindex,follow" />
                    <meta key="googlebot" name="googlebot" content="noindex,follow" />
                </Head>
            )}
            {title && (
                <Head>
                    <title>{combinedTitle}</title>
                    <meta name="twitter:title" key="twitter:title" content={title} />
                    <meta property="og:title" key="og:title" content={title} />
                </Head>
            )}
            {description && (
                <Head>
                    <meta name="description" content={description} />
                    <meta property="og:description" key="og:description" content={description} />
                    <meta name="twitter:description" key="twitter:description" content={description} />
                </Head>
            )}
            <Head>
                {keywords && <meta name="keywords" content={keywords} />}
                {children}
            </Head>
            <ReactHelmetFeeds title={title || META_DEFAULT_VALUES.title} path={pathForFeeds} />
        </>
    );
};

type ReactHelmetPlayApplicationProps = {
    router: NextRouter;
};

export function ReactHelmetPlayApplication({ router }: ReactHelmetPlayApplicationProps): JSX.Element {
    const pathname = pathNoQueryHash(router.asPath);

    const url = join(env.BASE_URL, pathname);

    return (
        <>
            <Head>
                {env.FLAG_NO_INDEX && <meta name="robots" content="noindex" />}
                <meta name="author" content="Sveriges Television AB, Stockholm, Sweden" />
                <meta property="og:site_name" key="og:site_name" content={META_DEFAULT_VALUES.title} />

                <meta property="og:image" key="og:image" content={META_DEFAULT_VALUES.image} />
                <meta property="og:url" key="og:url" content={url} />

                <meta name="twitter:card" key="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" key="twitter:site" content="@svt" />
                <meta name="twitter:url" key="twitter:url" content={url} />
                <meta name="twitter:image:width" key="twitter:image:width" content="480" />
                <meta name="twitter:image:height" key="twitter:image:height" content="270" />
                <meta name="twitter:image:src" key="twitter:image:src" content={META_DEFAULT_VALUES.image} />
                <link rel="canonical" key="canonical" href={url} />
            </Head>
            <ReactHelmetPlay
                description={META_DEFAULT_VALUES.description}
                title={META_DEFAULT_VALUES.title}
                keywords={META_DEFAULT_VALUES.keywords}
            />
        </>
    );
}
